import React from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { PlatformLogo, PlatformId } from './PlatformIcons';

/**
 * The "sent via" chip on a dashboard card.
 *
 * Visual idea: a small wax-seal stamp rather than a flat SaaS pill — it
 * lands on the card at a slight tilt, like a stamp pressed onto a case
 * file, echoing the report/evidence framing the rest of Dashboard already
 * uses (COMPLETED badge, VIEW REPORT button, the red/orange top hairline).
 * Straightens out on hover, as if you'd picked it up to look closer.
 */

type Ink = { text: string; bg: string; border: string };

const INK: Record<PlatformId, Ink> = {
  WhatsApp: { text: '#1da851', bg: '#25d3661a', border: '#25d36645' },
  Snapchat: { text: '#93a300', bg: '#fffc001a', border: '#fffc0055' },
  Telegram: { text: '#0088cc', bg: '#0088cc1a', border: '#0088cc45' },
  Instagram: { text: '#c13584', bg: '#dd2a7b1a', border: '#dd2a7b45' },
  TikTok: { text: '#e0244f', bg: '#fe2c551a', border: '#fe2c5545' },
  SMS: { text: 'var(--d-muted2)', bg: 'var(--d-stat-bg)', border: 'var(--d-border2)' },
};

export function PlatformBadge({ platform, delay = 0 }: { platform: PlatformId; delay?: number }) {
  const reduceMotion = useReducedMotion();
  const ink = INK[platform];

  return (
    <motion.span
      initial={reduceMotion ? false : { opacity: 0, scale: 0.5, rotate: -14, y: 3 }}
      animate={{ opacity: 1, scale: 1, rotate: -2, y: 0 }}
      whileHover={{ rotate: 0, y: -1 }}
      transition={
        reduceMotion
          ? { duration: 0 }
          : { type: 'spring', stiffness: 460, damping: 15, delay }
      }
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 10px 3px 3px',
        borderRadius: 9999,
        background: ink.bg,
        border: `1.5px solid ${ink.border}`,
        transformOrigin: '20% 60%',
        cursor: 'default',
      }}>
      <PlatformLogo platform={platform} size={19} />
      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.4, color: ink.text }}>
        {platform}
      </span>
    </motion.span>
  );
}

export default PlatformBadge;
